import { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Search, X, ChevronDown } from 'lucide-react';

// Selector con búsqueda por texto. options: [{ value, label, hint }]
export function SearchableSelect({
  value, onChange, options = [], placeholder = 'Buscar...', disabled = false,
  emptyLabel = 'Sin resultados', testId, maxResults = 50,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  const selected = options.find(o => o.value === value) || null;

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter(o => `${o.label} ${o.hint || ''}`.toLowerCase().includes(q))
      : options;
    return list.slice(0, maxResults);
  }, [query, options, maxResults]);

  const pick = (opt) => { onChange(opt.value); setQuery(''); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      {open ? (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" />
          <Input autoFocus className="pl-8" value={query} placeholder={placeholder}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filtered.length) { e.preventDefault(); pick(filtered[0]); }
              if (e.key === 'Escape') setOpen(false);
            }}
            data-testid={testId ? `${testId}-input` : undefined} />
        </div>
      ) : (
        <button type="button" disabled={disabled} onClick={() => { setOpen(true); setQuery(''); }}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
          data-testid={testId}>
          <span className={selected ? '' : 'text-muted-foreground'}>
            {selected ? selected.label : placeholder}
          </span>
          <span className="flex items-center gap-1">
            {selected && (
              <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onChange(''); }} />
            )}
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </span>
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-popover shadow-lg"
          data-testid={testId ? `${testId}-list` : undefined}>
          {filtered.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{emptyLabel}</div>}
          {filtered.map(o => (
            <button key={o.value} type="button" onClick={() => pick(o)}
              className={`flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-accent ${o.value === value ? 'bg-accent/60' : ''}`}
              data-testid={testId ? `${testId}-option-${o.value}` : undefined}>
              <span>{o.label}</span>
              {o.hint && <span className="text-[11px] text-muted-foreground">{o.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
